import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { HubspotEmailPage, QueryHubspotIntegrationEmailsArgs } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import clientUtil from 'utils/clientUtil';

export interface HubSpotEmailWidgetBody {
  value?: string;
}

export interface HubSpotEmailWidget {
  body?: HubSpotEmailWidgetBody;
  child_css?: Record<string, unknown>;
  css?: Record<string, unknown>;
  id: string;
  label?: string;
  name?: string;
  smart_type?: string | null;
  styles?: Record<string, unknown>;
  type?: string;
}

export interface HubSpotEmailContent {
  widgets: Record<string, HubSpotEmailWidget>;
  templatePath?: string;
}

export interface HubSpotEmailWebVersion {
  domain: string;
  isPageRedirected: boolean;
  slug: string;
  url: string;
}

export interface HubSpotEmailFrom {
  fromName: string;
  replyTo: string;
}

export interface HubSpotEmailSubscriptionDetails {
  officeLocationId: string;
  subscriptionId: string;
}

export interface HubSpotEmailTo {
  suppressGraymail: boolean;
}

export interface HubSpotMarketingEmail {
  id: string;
  name: string;
  subject: string;
  publishDate: string;
  isTransactional: boolean;
  type: string;
  content: HubSpotEmailContent;
  businessUnitId: string;
  webversion: HubSpotEmailWebVersion;
  archived: boolean;
  createdAt: string;
  jitterSendTime: boolean;
  from: HubSpotEmailFrom;
  state: string;
  createdById: string;
  updatedAt: string;
  publishedAt: string;
  publishedById: string;
  isPublished: boolean;
  updatedById: string;
  subscriptionDetails: HubSpotEmailSubscriptionDetails;
  activeDomain: string;
  to: HubSpotEmailTo;
  subcategory: string;
  language?: string;
}

type TVariables = QueryHubspotIntegrationEmailsArgs;
type TData = HubSpotMarketingEmail;

export const useHubSpotIntegrationGetEmailMutation = (
  options: Omit<UseMutationOptions<TData, TGenericError, TVariables, unknown>, 'mutationFn'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query hubspotIntegrationEmails(
      $emailId: ID
      $page: Int
      $perPage: Int
      $skipPagination: Boolean
    ) {
      hubspotIntegrationEmails(
        emailId: $emailId
        page: $page
        perPage: $perPage
        skipPagination: $skipPagination
      ) {
        nodes {
          emailId
          jsonContent
          language
          name
          publishDate
        }
        hasNextPage
        nodesCount
        pagesCount
      }
    }
  `;

  return useMutation<TData, TGenericError, TVariables, unknown>({
    mutationFn: async (variables: TVariables): Promise<TData> =>
      (
        (await client.request({
          data: {
            gql: graphql,
            variables
          }
        })) as HubspotEmailPage
      ).nodes
        ?.map((item) => JSON.parse(item.jsonContent))
        ?.at(0),
    ...options
  });
};
